import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  effect,
  inject,
  input,
  resource,
  viewChild,
} from '@angular/core';
import { sanitizePreviewLinks } from '@frontendlib/common/utils/sanitize-preview-links';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { PreviewErrorComponent } from '../preview-error/preview-error.component';
import { TopProgressService } from '@modules/top-progress/top-progress.service';

interface RenderParams {
  readonly blob: Blob | null;
  readonly container: HTMLDivElement | undefined;
}

@Component({
  selector: 'app-word-preview',
  templateUrl: './word-preview.component.html',
  styleUrl: './word-preview.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [LoadingIndicatorComponent, PreviewErrorComponent],
})
export class WordPreviewComponent {
  public readonly blob = input<Blob | null>(null);

  private readonly container = viewChild<ElementRef<HTMLDivElement>>('container');
  private readonly topProgress = inject(TopProgressService);

  protected readonly render = resource<boolean, RenderParams>({
    params: () => ({ blob: this.blob(), container: this.container()?.nativeElement }),
    defaultValue: false,
    loader: async ({ params, abortSignal }) => {
      const { blob, container } = params;
      if (!blob || !container) {
        return false;
      }
      const docx = await this.topProgress.track(import('docx-preview'));
      abortSignal.throwIfAborted();
      // Render off-DOM so an aborted render can be discarded without touching `container`
      // (docx-preview's renderAsync has no abort signal and mutates the DOM as it runs).
      const staging = document.createElement('div');
      await docx.renderAsync(blob, staging, undefined, {
        className: 'docx-preview',
        inWrapper: true,
        breakPages: true,
        experimental: true,
        trimXmlDeclaration: true,
        useBase64URL: true,
      });
      abortSignal.throwIfAborted();
      sanitizePreviewLinks(staging);
      container.replaceChildren(...Array.from(staging.childNodes));
      return true;
    },
  });

  public constructor() {
    effect(() => {
      const err = this.render.error();
      if (err) {
        console.warn('[word-preview] render failed', err);
      }
    });
    inject(DestroyRef).onDestroy(() => {
      const container = this.container()?.nativeElement;
      if (container) {
        container.replaceChildren();
      }
    });
  }
}
